/**
 * サロンボード互換機能への既存データ移行スクリプト
 * 
 * 既存のCustomerとReservationデータを拡張型（ExtendedCustomer, ExtendedReservation）へ移行します。
 * このスクリプトは一度だけ実行されることを想定しています。
 */

import { 
  ExtendedCustomer, 
  ExtendedReservation,
  ReservationSource,
  VisitProcessingStatus,
  CancellationPolicyType
} from '../types/salonboard';
import {
  getCustomers,
  saveCustomers,
  getReservations,
  saveReservations,
  Customer
} from '../utils/localStorage';
import {
  calculateVisitProcessingDeadline,
  mapOldStatusToDetailed,
  calculatePoints,
  SequenceManager
} from '../utils/salonboard-utils';

const STORAGE_KEYS = {
  CUSTOMERS: 'salonhub_customers',
  RESERVATIONS: 'salonhub_reservations',
  MIGRATION_COMPLETED: 'salonhub_migration_completed'
} as const;

/**
 * 移行が既に完了しているかチェック
 */
export function isMigrationCompleted(): boolean {
  return localStorage.getItem(STORAGE_KEYS.MIGRATION_COMPLETED) === 'true';
}

/**
 * 移行完了フラグを設定
 */
function setMigrationCompleted(): void {
  localStorage.setItem(STORAGE_KEYS.MIGRATION_COMPLETED, 'true');
}

/**
 * 顧客データを拡張型に移行
 */
function migrateCustomers(customers: Customer[]): ExtendedCustomer[] {
  const sequenceManager = SequenceManager.getInstance();
  
  return customers.map((customer) => {
    // 既に拡張されている場合はそのまま返す
    if ('customerCode' in customer) {
      return customer as ExtendedCustomer;
    }
    
    const extendedCustomer: ExtendedCustomer = {
      ...(customer as any),
      customerCode: sequenceManager.getNextCustomerCode(),
      messageSettings: {
        email: true,
        myPage: true,
        push: false,
        sms: false
      },
      deliveryHistory: [],
      preSystemVisitCount: 0,
      customFields: {
        other1: '',
        other2: '',
        other3: ''
      },
      mergeHistory: {
        mergedFrom: [],
      },
      mergeCandidates: []
    };
    
    return extendedCustomer;
  });
}

/**
 * 予約データを拡張型に移行
 */
function migrateReservations(reservations: any[]): ExtendedReservation[] {
  const sequenceManager = SequenceManager.getInstance();
  
  return reservations.map((reservation) => {
    // 既に拡張されている場合はそのまま返す
    if ('reservationNumber' in reservation) {
      return reservation as ExtendedReservation;
    }
    
    // 予約経路を推測（既存データからは判断できないため、デフォルトを設定）
    const source: ReservationSource = 'PHONE'; // デフォルトは電話予約
    const prefix = 'YE'; // 電話予約のプレフィックス
    
    const extendedReservation: ExtendedReservation = {
      ...reservation,
      reservationNumber: sequenceManager.getNextReservationNumber(prefix),
      source,
      detailedStatus: mapOldStatusToDetailed(reservation.status),
      amount: {
        subtotal: reservation.amount || 0,
        tax: Math.floor((reservation.amount || 0) * 0.1),
        total: reservation.amount || 0,
        discount: 0,
        discountReason: undefined
      },
      visitProcessing: {
        status: 'PENDING' as VisitProcessingStatus,
        deadline: calculateVisitProcessingDeadline(new Date(reservation.date))
      },
      points: {
        earned: calculatePoints({ total: reservation.amount || 0 }),
        used: 0
      },
      cancellationPolicy: {
        type: 'SAME_DAY_50' as CancellationPolicyType,
        rate: 50
      },
      actualStartTime: undefined,
      actualEndTime: undefined,
      treatmentRecord: undefined,
      medicalCheckCompleted: false,
      note: reservation.note || ''
    };
    
    return extendedReservation;
  });
}

/**
 * 既存データの移行を実行
 */
export async function migrateExistingData(): Promise<{
  success: boolean;
  message: string;
  details?: {
    migratedCustomers: number;
    migratedReservations: number;
  };
}> {
  try {
    // 既に移行済みの場合はスキップ
    if (isMigrationCompleted()) {
      return {
        success: true,
        message: 'データ移行は既に完了しています。'
      };
    }
    
    console.log('サロンボード互換機能へのデータ移行を開始します...');
    
    // Step 1: 顧客データの移行
    const customers = getCustomers();
    const extendedCustomers = migrateCustomers(customers);
    saveCustomers(extendedCustomers);
    console.log(`${extendedCustomers.length}件の顧客データを移行しました。`);
    
    // Step 2: 予約データの移行
    const reservations = getReservations();
    const extendedReservations = migrateReservations(reservations);
    saveReservations(extendedReservations);
    console.log(`${extendedReservations.length}件の予約データを移行しました。`);
    
    // 移行完了フラグを設定
    setMigrationCompleted();
    
    return {
      success: true,
      message: 'データ移行が正常に完了しました。',
      details: {
        migratedCustomers: extendedCustomers.length,
        migratedReservations: extendedReservations.length
      }
    };
  } catch (error) {
    console.error('データ移行中にエラーが発生しました:', error);
    
    return {
      success: false,
      message: `データ移行中にエラーが発生しました: ${error instanceof Error ? error.message : '不明なエラー'}`
    };
  }
}

/**
 * 移行をロールバック（開発/テスト用）
 */
export function rollbackMigration(): void {
  // 移行完了フラグを削除
  localStorage.removeItem(STORAGE_KEYS.MIGRATION_COMPLETED);
  
  // 注意: 実際のデータは元に戻せないため、必要に応じて手動でバックアップから復元する必要があります
  console.warn('移行完了フラグを削除しました。データの復元は手動で行ってください。');
}

/**
 * 移行状態をチェック（デバッグ用）
 */
export function checkMigrationStatus(): {
  migrated: boolean;
  customerCount: number;
  reservationCount: number;
  sampleCustomer?: any;
  sampleReservation?: any;
} {
  const migrated = isMigrationCompleted();
  const customers = getCustomers();
  const reservations = getReservations();
  
  return {
    migrated,
    customerCount: customers.length,
    reservationCount: reservations.length,
    sampleCustomer: customers[0],
    sampleReservation: reservations[0]
  };
}